import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, writeBatch, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase.js';

const ALL_DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getAvailableDaysForMatchup(homeID, awayID, teamRestrictions, gameDays) {
  const getDays = (teamID) => {
    const r = teamRestrictions[teamID];
    if (!r || !r.type || r.type === 'none' || !r.days || r.days.length === 0) return gameDays;
    if (r.type === 'whitelist') return gameDays.filter(d => r.days.includes(d));
    if (r.type === 'blacklist') return gameDays.filter(d => !r.days.includes(d));
    return gameDays;
  };
  const homeDays = getDays(homeID);
  const awayDays = getDays(awayID);
  const intersection = homeDays.filter(d => awayDays.includes(d));
  return intersection.length > 0 ? intersection : gameDays; // fallback to all days
}

// ── Schedule CSV import ──────────────────────────────────────────────────────

const IMPORT_TEMPLATE_CSV =
  'Date,Time,Location,Home,Away\n' +
  '11/02/2026,6:30 AM,SBCA,Team Name,Other Team Name\n';

// Minimal CSV parser: handles quoted fields, escaped quotes, CRLF line endings
// and the byte-order mark Excel adds when saving as "CSV UTF-8".
function parseCSV(text) {
  text = text.replace(/^﻿/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); rows.push(row);
      row = []; field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// The league plays in Boston, so imported times are Eastern no matter which
// timezone the admin's browser is in.
const LEAGUE_TIME_ZONE = 'America/New_York';

function easternDate(y, m, day, h, min) {
  const guess = Date.UTC(y, m - 1, day, h, min);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TIME_ZONE, hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
  }).formatToParts(new Date(guess));
  const get = (type) => Number(parts.find(p => p.type === type).value);
  const guessAsEastern = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return new Date(guess - (guessAsEastern - guess));
}

// Accepts 11/02/2026, 11/2/26, 2026-11-02, "Monday, 11/02/2026" (as written by
// the export) or anything else Date can parse, e.g. "Nov 2, 2026". Time accepts
// "6:30 AM", "6:30AM", "18:30" or "6:30:00 AM"; a blank time uses defaultTime.
function parseImportDate(dateStr, timeStr, defaultTime) {
  const d = (dateStr || '').trim().replace(/^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?,?\s+/i, '');
  let y, m, day, match;
  if ((match = d.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    [y, m, day] = match.slice(1).map(Number);
  } else if ((match = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/))) {
    [m, day, y] = match.slice(1).map(Number);
    if (y < 100) y += 2000;
  } else {
    const parsed = new Date(d);
    if (!d || isNaN(parsed)) return null;
    [y, m, day] = [parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()];
  }

  const t = (timeStr || '').trim() || defaultTime;
  const tm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!tm) return null;
  let h = Number(tm[1]);
  const min = Number(tm[2]);
  if (tm[3]) {
    const pm = tm[3].toLowerCase() === 'pm';
    if (h < 1 || h > 12) return null;
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
  }
  if (h > 23 || min > 59) return null;

  // Reject rollovers like 02/30 becoming March 2.
  const check = new Date(y, m - 1, day);
  if (check.getMonth() !== m - 1 || check.getDate() !== day) return null;
  return easternDate(y, m, day, h, min);
}

const LOCATION_ALIASES = [
  ['sbca', ['sbca', 'south boston catholic']],
  ['b&gc', ['b&gc', 'bgc', 'boys & girls', 'boys and girls']],
  ['jpt-school', ['tynan', 'jpt']],
  ['condon', ['condon']],
];

function parseImportLocation(str) {
  const s = (str || '').trim().toLowerCase();
  if (!s) return null;
  const hit = LOCATION_ALIASES.find(([, aliases]) => aliases.some(a => s.includes(a)));
  return hit ? hit[0] : null;
}

const AdminSeasonGames = () => {
  const { isReady, currentUser } = useAdminGuard();
  const canEditCalendar = currentUser?.email === 'ccrane2@alumni.nd.edu';
  const { season } = useParams();
  const GAMES_PER_TEAM = utils.getGamesPerTeamForSeason(season);

  const [gamesPerTeam, setGamesPerTeam] = useState(GAMES_PER_TEAM);
  const [gamesEditable, setGamesEditable] = useState(false)
  const [dataReady, setDataReady] = useState(false);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [frPeterTeamID, setFrPeterTeamID] = useState(null);
  // The season doc's `conferences` list decides whether this is a conference
  // season (same signal StandingsSection uses). Empty means one league-wide pool.
  const [seasonConferences, setSeasonConferences] = useState([]);
  const seasonHasConferences = seasonConferences.length > 0;
  const [importResult, setImportResult] = useState(null);
  const [importLocation, setImportLocation] = useState('b&gc'); // used when a row has no Location

  const [scheduleConfig, setScheduleConfig] = useState({
    startDate: '',
    startTime: '06:30',
    blackoutDates: [],
    gameDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  });
  const [showScheduleConfig, setShowScheduleConfig] = useState(false);

  const [teamRestrictions, setTeamRestrictions] = useState({});
  // { [teamID]: { type: 'whitelist'|'blacklist', days: ['Monday', ...] } }
  const [showTeamRestrictions, setShowTeamRestrictions] = useState(false);

  // Temporary state for blackout date input
  const [newBlackoutDate, setNewBlackoutDate] = useState('');

  const [confirm, setConfirm] = useState({ open: false, message: '', onConfirm: null });
  const askConfirm = (message, action) => setConfirm({ open: true, message, onConfirm: action });
  const closeConfirm = () => setConfirm({ open: false, message: '', onConfirm: null });

  const [calDate, setCalDate] = useState({ year: 2026, month: 4 }); // 0-indexed month (4 = May)
  const [selectedGame, setSelectedGame] = useState(null);

  // Months to allow navigating: every month from the first game through the
  // last, or the start date plus the following two months when there are no games.
  const calMonths = (() => {
    const dates = games.length > 0
      ? games.map(g => g.date)
      : [scheduleConfig.startDate ? new Date(scheduleConfig.startDate + 'T12:00:00') : new Date()];
    const monthIndex = d => d.getFullYear() * 12 + d.getMonth();
    const first = Math.min(...dates.map(monthIndex));
    const last = games.length > 0 ? Math.max(...dates.map(monthIndex)) : first + 2;
    const months = [];
    for (let i = first; i <= last; i++) months.push({ year: Math.floor(i / 12), month: i % 12 });
    return months;
  })();

  const calIdx = calMonths.findIndex(m => m.year === calDate.year && m.month === calDate.month);

  // Jump the calendar to the schedule's first month whenever the shown month
  // falls outside the schedule (e.g. after loading or importing games).
  const firstCalMonth = calMonths[0];
  useEffect(() => {
    if (calIdx === -1 && firstCalMonth) setCalDate(firstCalMonth);
  }, [calIdx, firstCalMonth?.year, firstCalMonth?.month]); // eslint-disable-line react-hooks/exhaustive-deps

  const rescheduleGame = async (game, newDay) => {
    const newDate = new Date(calDate.year, calDate.month, newDay,
      game.date.getHours(), game.date.getMinutes(), 0);
    const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const updated = { ...game, date: newDate, weekday: DAY_NAMES[newDate.getDay()] };
    setGames(prev => prev.map(g => g === game ? updated : g));
    setSelectedGame(null);
    if (game.id) {
      await updateDoc(doc(db, 'games', game.id), { date: Timestamp.fromDate(newDate) });
    }
  };

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      await getCurrentSeasonData();
      await getTeams();
      await getGames();
      setDataReady(true);
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentSeasonData = async () => {
    const seasonRef = doc(db, "seasons", season);
    const seasonSnapshot = await getDoc(seasonRef);
    const seasonData = seasonSnapshot.data();

    if (!seasonData) return;

    setSeasonConferences(Array.isArray(seasonData.conferences) ? seasonData.conferences.filter(Boolean) : []);

    if (seasonData.scheduleConfig) {
      setScheduleConfig(seasonData.scheduleConfig);
      if (seasonData.scheduleConfig.teamRestrictions) {
        setTeamRestrictions(seasonData.scheduleConfig.teamRestrictions);
      }
    }
  }

  const getTeams = async () => {
    const teamsRef = collection(db, "teams");
    const teamsSnapshot = await getDocs(teamsRef);
    const teamsData = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const seasonTeamsRef = collection(db, "seasonTeams");
    const seasonTeamsSnapshot = await getDocs(seasonTeamsRef);
    const seasonTeamsData = seasonTeamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const mergedTeams = teamsData
      .filter(team => seasonTeamsData.some(st => st.teamID === team.id && st.season == season))
      .map(team => {
        const seasonTeam = seasonTeamsData.find(st => st.teamID === team.id && st.season == season);
        return { ...seasonTeam, ...team };
      });

    setTeams(mergedTeams);

    // Determine Fr. Peter's team
    const fpTeam = mergedTeams.find(t => Array.isArray(t.players) && t.players.includes('luanwfmcg6e2u13p44'));
    setFrPeterTeamID(fpTeam ? fpTeam.id : null);

  }

  const getGames = async () => {
    const gamesRef = collection(db, "games");
    const q = query(gamesRef, where("season", "==", parseInt(season)));
    const gamesSnapshot = await getDocs(q);

    let gamesData = gamesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // set gamesEditable to true if all games have status pending or if there are no games
    let allGamesPending = gamesData.every(game => game.status === 'pending');
    setGamesEditable(allGamesPending || gamesData.length === 0);

    let games = gamesData.map(game => ({
      ...game,
      date: game.date.toDate()
    }));
    games.sort((a, b) => a.date - b.date);

    // add weekday to each game
    games = games.map(game => ({
      ...game,
      weekday: game.date.toLocaleString("en-US", { weekday: 'long' })
    }));

    setGames(games);
  };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatDate = (date) => {
    return date.toLocaleString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Build all intra-conference matchups.
  // If conference size == gamesPerTeam + 1: pure round-robin (each team plays all opponents once).
  // If conference size < gamesPerTeam + 1: round-robin + rematches to fill the gap.
  const buildConferenceMatchups = (confTeams, numGames) => {
    const n = confTeams.length;
    if (n < 2) return [];

    // Full round-robin (all unique pairs)
    const pairs = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        pairs.push({ homeTeam: confTeams[i], awayTeam: confTeams[j] });
      }
    }

    const inConfOpponents = n - 1;
    if (numGames <= inConfOpponents) return pairs; // full or partial round-robin sufficient

    // Need rematches: each team needs (numGames - inConfOpponents) extra games
    const extraPerTeam = numGames - inConfOpponents;
    const totalExtra = (n * extraPerTeam) / 2;
    const shuffled = [...confTeams].sort(() => Math.random() - 0.5);
    const rematches = [];
    for (let rep = 0; rep < extraPerTeam; rep++) {
      for (let i = 0; i < shuffled.length && rematches.length < totalExtra; i += 2) {
        if (i + 1 < shuffled.length) {
          rematches.push({ homeTeam: shuffled[i], awayTeam: shuffled[i + 1] });
        } else {
          // Odd team count edge case: last team rematches first
          rematches.push({ homeTeam: shuffled[0], awayTeam: shuffled[shuffled.length - 1] });
        }
      }
    }
    return [...pairs, ...rematches];
  };

  // Group flat matchup list into rounds where each team appears at most once per round.
  const groupMatchupsIntoRounds = (matchups) => {
    const remaining = [...matchups].sort(() => Math.random() - 0.5);
    const rounds = [];
    while (remaining.length > 0) {
      const round = [];
      const usedTeams = new Set();
      const leftover = [];
      for (const m of remaining) {
        if (!usedTeams.has(m.homeTeam.id) && !usedTeams.has(m.awayTeam.id)) {
          round.push(m);
          usedTeams.add(m.homeTeam.id);
          usedTeams.add(m.awayTeam.id);
        } else {
          leftover.push(m);
        }
      }
      if (round.length === 0) break;
      rounds.push(round);
      remaining.splice(0, remaining.length, ...leftover);
    }
    return rounds;
  };

  const createSchedule = () => {
    setGames([]);

    const eastTeams = [...teams].filter(t => t.conference === 'east').sort(() => Math.random() - 0.5);
    const westTeams = [...teams].filter(t => t.conference === 'west').sort(() => Math.random() - 0.5);

    if (eastTeams.length === 0 || westTeams.length === 0) {
      alert('Both conferences must have teams assigned before generating a schedule.');
      return;
    }

    if (!scheduleConfig.startDate) {
      alert('Please set a start date in Edit Schedule Config before generating.');
      return;
    }

    const eastMatchups = buildConferenceMatchups(eastTeams, gamesPerTeam);
    const westMatchups = buildConferenceMatchups(westTeams, gamesPerTeam);

    // Separate rematches from unique pairs so rematches are scheduled at the
    // end of the season rather than being randomly mixed into early weeks.
    const eastUniquePairs = eastTeams.length * (eastTeams.length - 1) / 2;
    const westUniquePairs = westTeams.length * (westTeams.length - 1) / 2;

    const eastRounds = [
      ...groupMatchupsIntoRounds(eastMatchups.slice(0, eastUniquePairs)),
      ...groupMatchupsIntoRounds(eastMatchups.slice(eastUniquePairs)),
    ];
    const westRounds = [
      ...groupMatchupsIntoRounds(westMatchups.slice(0, westUniquePairs)),
      ...groupMatchupsIntoRounds(westMatchups.slice(westUniquePairs)),
    ];

    const [year, month, day] = scheduleConfig.startDate.split('-').map(Number);
    const [hours, minutes] = (scheduleConfig.startTime || '06:30').split(':').map(Number);
    const startDate = new Date(year, month - 1, day, hours, minutes, 0);
    // Parse blackout dates as local time (not UTC) to avoid off-by-one timezone issues.
    const blackoutDatesConverted = scheduleConfig.blackoutDates.map(d => {
      const [yr, mo, dy] = d.split('-').map(Number);
      return new Date(yr, mo - 1, dy).toDateString();
    });

    const allGames = assignDatesToMatchups(
      eastRounds, westRounds, startDate, blackoutDatesConverted, scheduleConfig.gameDays
    );

    setGames(allGames);
  };

  // Load games from an Excel-saved CSV into the temp schedule. Columns are
  // matched by header name (any order, case-insensitive): Date, Time (optional),
  // Location (optional; blank uses the Default location picker), and either Home + Away or a single Teams column ("Home vs. Away",
  // or "Away @ Home") — the same format the export below writes. Teams match
  // by full name, abbreviation, or ID. Nothing is written to the database;
  // the imported games go through the usual Save as Pending / Scheduled steps.
  const importScheduleCSV = async (file) => {
    const fileName = file.name;
    const rows = parseCSV(await file.text());
    const fail = (errors) => setImportResult({ fileName, errors, warnings: [], count: 0 });

    if (rows.length < 2) return fail(['The file has no game rows.']);

    const header = rows[0].map(h => h.trim().toLowerCase());
    const col = (...names) => header.findIndex(h => names.includes(h));
    const dateCol = col('date', 'game date');
    const timeCol = col('time', 'start time', 'game time');
    const locationCol = col('location', 'venue', 'gym');
    const homeCol = col('home', 'home team');
    const awayCol = col('away', 'away team');
    const teamsCol = col('teams', 'matchup', 'game');

    const missing = [];
    if (dateCol === -1) missing.push('Date');
    if ((homeCol === -1 || awayCol === -1) && teamsCol === -1) missing.push('Home + Away (or Teams)');
    if (missing.length > 0) {
      return fail([`Missing column(s): ${missing.join(', ')}. Found: ${rows[0].join(', ')}`]);
    }

    const teamLookup = new Map();
    teams.forEach(t => {
      [t.name, t.nameAbbv, t.id].filter(Boolean).forEach(key => teamLookup.set(String(key).trim().toLowerCase(), t));
    });
    const findTeam = (str) => teamLookup.get((str || '').trim().toLowerCase());

    const DAY_NAME = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const defaultTime = scheduleConfig.startTime || '06:30';
    const errors = [];
    const imported = [];

    rows.slice(1).forEach((cells, i) => {
      const line = i + 2; // spreadsheet row number (header is row 1)
      const cell = (idx) => (idx === -1 ? '' : (cells[idx] || '').trim());
      if (cells.every(c => c.trim() === '')) return;

      let homeName, awayName;
      if (homeCol !== -1 && awayCol !== -1) {
        homeName = cell(homeCol);
        awayName = cell(awayCol);
      } else {
        const matchup = cell(teamsCol);
        const atParts = matchup.split(/\s+@\s+/);
        const vsParts = matchup.split(/\s+(?:vs\.?|v\.?)\s+/i);
        if (atParts.length === 2) [awayName, homeName] = atParts;
        else if (vsParts.length === 2) [homeName, awayName] = vsParts;
        else { errors.push(`Row ${line}: can't read teams from "${matchup}" (expected "Home vs. Away").`); return; }
      }

      const home = findTeam(homeName);
      const away = findTeam(awayName);
      const date = parseImportDate(cell(dateCol), cell(timeCol), defaultTime);
      const location = cell(locationCol) ? parseImportLocation(cell(locationCol)) : importLocation;

      const rowErrors = [];
      if (!home) rowErrors.push(`unknown home team "${homeName}"`);
      if (!away) rowErrors.push(`unknown away team "${awayName}"`);
      if (home && away && home.id === away.id) rowErrors.push('home and away are the same team');
      if (!date) rowErrors.push(`can't read date/time "${[cell(dateCol), cell(timeCol)].filter(Boolean).join(' ')}"`);
      if (!location) rowErrors.push(`unknown location "${cell(locationCol)}" (use SBCA, B&GC, Tynan, or Condon)`);
      if (rowErrors.length > 0) { errors.push(`Row ${line}: ${rowErrors.join('; ')}.`); return; }

      imported.push({
        date,
        dateFormatted: formatDate(date),
        dateISO: date.toISOString(),
        weekday: DAY_NAME[date.getDay()],
        location,
        homeTeamID: home.id,
        homeConference: home.conference || '',
        awayTeamID: away.id,
        awayConference: away.conference || '',
        season,
      });
    });

    if (errors.length > 0) return fail(errors);
    if (imported.length === 0) return fail(['The file has no game rows.']);

    imported.sort((a, b) => a.date - b.date);

    // Week numbers count Monday-start weeks from the first game's week.
    const mondayOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
    const firstMonday = mondayOf(imported[0].date);
    imported.forEach(g => {
      g.week = Math.round((mondayOf(g.date) - firstMonday) / (7 * 24 * 60 * 60 * 1000)) + 1;
    });

    // Non-blocking sanity checks.
    const warnings = [];
    const teamName = (id) => teams.find(t => t.id === id)?.name || id;
    const teamDays = new Map();
    const slots = new Set();
    imported.forEach(g => {
      const day = g.date.toDateString();
      [g.homeTeamID, g.awayTeamID].forEach(id => {
        const key = `${id}_${day}`;
        if (teamDays.has(key)) warnings.push(`${teamName(id)} plays twice on ${day}.`);
        teamDays.set(key, true);
      });
      const slot = `${g.location}_${g.date.getTime()}`;
      if (slots.has(slot)) warnings.push(`Two games at ${utils.getLocationName(g.location, true)} at the same time on ${formatDate(g.date)}.`);
      slots.add(slot);
    });
    const perTeam = teams.map(t => ({
      name: t.name,
      count: imported.filter(g => g.homeTeamID === t.id || g.awayTeamID === t.id).length,
    }));
    if (new Set(perTeam.map(p => p.count)).size > 1) {
      warnings.push(`Teams have different game counts: ${perTeam.map(p => `${p.name} ${p.count}`).join(', ')}.`);
    }

    setSelectedGame(null);
    setGames(imported);
    setImportResult({ fileName, errors: [], warnings, count: imported.length });
  };

  const downloadImportTemplate = () => {
    const blob = new Blob([IMPORT_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `season_${season}_schedule_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Assign dates to all matchups.
  //
  // Hard constraints (never violated):
  //   1. No games on blackout dates
  //   2. Blacklisted days are never used
  //   3. Only whitelisted days are used
  // Soft constraint (enforced first, relaxed only if truly impossible):
  //   4. At least 2 clear days between a team's games
  //      Mon/Tue played → next game Thu/Fri (+3 days)
  //      Wed/Thu/Fri played → next game following Monday (+5/+4/+3 days)
  //
  // For each round, each matchup searches forward from the round's anchor week
  // for the earliest valid slot, so restricted teams never get wrong days.
  const assignDatesToMatchups = (eastRounds, westRounds, startDate, blackoutDates, gameDays) => {
    const DAY_NAME = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const DAY_IDX = {Sunday:0,Monday:1,Tuesday:2,Wednesday:3,Thursday:4,Friday:5,Saturday:6};
    const gameDaySet = new Set(gameDays.map(d => DAY_IDX[d]));

    const isBlackout = (d) => blackoutDates.includes(d.toDateString());
    const isGameDay  = (d) => gameDaySet.has(d.getDay());
    const minGapDays = (dow) => dow <= 2 ? 3 : (8 - dow); // Mon/Tue→+3, Wed→+5, Thu→+4, Fri→+3

    // venue+date key to prevent double-booking a gym on the same day
    const usedSlots = new Set();
    const teamLastGame = {}; // teamID → Date

    const gapOk = (d, teamID) => {
      const last = teamLastGame[teamID];
      if (!last) return true;
      return d >= addDays(last, minGapDays(last.getDay()));
    };

    const makeGame = (m, slot, venue, weekNum) => ({
      date: new Date(slot),
      dateFormatted: formatDate(slot),
      dateISO: slot.toISOString(),
      weekday: DAY_NAME[slot.getDay()],
      week: weekNum,
      location: venue,
      homeTeamID: m.homeTeam.id,
      homeConference: m.homeTeam.conference,
      awayTeamID: m.awayTeam.id,
      awayConference: m.awayTeam.conference,
      season,
    });

    // Find a valid slot for matchup m at venue, searching from anchor forward.
    // withGap=true enforces constraint 4; withGap=false relaxes only that.
    // Constraints 1–3 are always enforced regardless of withGap.
    const findSlot = (anchor, venue, m, withGap) => {
      const availDays = getAvailableDaysForMatchup(m.homeTeam.id, m.awayTeam.id, teamRestrictions, gameDays);
      const isEast = venue === 'sbca';
      let d = new Date(anchor);
      const limit = addDays(startDate, 365);
      while (d <= limit) {
        if (isGameDay(d) && !isBlackout(d)) {
          const dow = d.getDay();
          if (isEast && dow === 3) { d = addDays(d, 1); continue; } // SBCA: no Wednesday
          const dayName = DAY_NAME[dow];
          if (availDays.includes(dayName)                                   // hard: whitelist/blacklist
              && !usedSlots.has(`${d.toDateString()}_${venue}`)             // hard: venue free
              && (!withGap || (gapOk(d, m.homeTeam.id) && gapOk(d, m.awayTeam.id)))) { // soft
            return new Date(d);
          }
        }
        d = addDays(d, 1);
      }
      return null;
    };

    // Flatten all matchups, tagged with round and venue, then sort:
    //   primary: round (earlier rounds first)
    //   secondary: restriction strictness (most restricted first — so JP II
    //              grabs Tuesday before unrestricted teams consume it)
    const maxRounds = Math.max(eastRounds.length, westRounds.length);
    const allMatchups = [];
    for (let r = 0; r < maxRounds; r++) {
      for (const m of (eastRounds[r] || [])) allMatchups.push({ ...m, venue: 'sbca', round: r });
      for (const m of (westRounds[r] || [])) allMatchups.push({ ...m, venue: 'b&gc', round: r });
    }
    allMatchups.sort((a, b) => {
      if (a.round !== b.round) return a.round - b.round;
      const aD = getAvailableDaysForMatchup(a.homeTeam.id, a.awayTeam.id, teamRestrictions, gameDays);
      const bD = getAvailableDaysForMatchup(b.homeTeam.id, b.awayTeam.id, teamRestrictions, gameDays);
      return aD.length - bD.length;
    });

    const allGames = [];
    for (const m of allMatchups) {
      const anchor = addDays(startDate, m.round * 7);
      // Try with gap first; fall back to no-gap (still respecting whitelist/blacklist/blackouts)
      const slot = findSlot(anchor, m.venue, m, true) || findSlot(anchor, m.venue, m, false);
      if (!slot) continue;
      usedSlots.add(`${slot.toDateString()}_${m.venue}`);
      teamLastGame[m.homeTeam.id] = slot;
      teamLastGame[m.awayTeam.id] = slot;
      allGames.push(makeGame(m, slot, m.venue, m.round + 1));
    }

    return allGames.sort((a, b) => a.date - b.date);
  };

  const handleGamesPerTeamChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setGamesPerTeam(value);
    }
  };

  async function deletePendingGamesForSeason () {
    const gamesRef = collection(db, "games");
    const pendingQuery = query(
      gamesRef,
      where("season", "==", parseInt(season)),
      where("status", "==", "pending")
    );

    const pendingSnapshot = await getDocs(pendingQuery);
    if (!pendingSnapshot.empty) {
      const deleteBatch = writeBatch(db);
      pendingSnapshot.docs.forEach(docSnap => {
        deleteBatch.delete(docSnap.ref);
      });
      await deleteBatch.commit();
    }
  }

  async function saveGamesAsPending() {
    if (games.length === 0) {
      alert("No games to save.");
      return;
    }

    await deletePendingGamesForSeason()

    const normalizedGames = games.map(g => ({
      awayTeamPlaceholder: '',
      awayTeamID: g.awayTeamID,
      date: Timestamp.fromDate(g.date),
      homeTeamPlaceholder: '',
      homeTeamID: g.homeTeamID,
      id: utils.createNewIDForGame(),
      location: g.location,
      playoffRoundOrder: -1,
      playoffTags: [],
      referees: [],
      score: { away: 0, home: 0 },
      season: parseInt(season),
      status: 'pending',
      type: 'regular_season',
      winner: ''
    }));

    // Create Firestore batch
    const batch = writeBatch(db);
    const gamesRef = collection(db, "games");

    normalizedGames.forEach(game => {
      const gameDoc = doc(gamesRef, game.id);
      batch.set(gameDoc, game);
    });

    // Commit batch
    try {
      await batch.commit();
      // Reflect IDs back into local state so they're visible in the game list
      setGames(normalizedGames.map(g => ({ ...g, date: g.date.toDate() })));
      alert(`${normalizedGames.length} games saved as pending.`);
    } catch (err) {
      console.error("Error saving games:", err);
      alert(`Error saving games: ${err.message}`);
    }
  }

  async function saveGames() {
    if (games.length === 0) {
      alert("No games to save.");
      return;
    }

    const gamesRef = collection(db, "games");
    const q = query(
      gamesRef,
      where("season", "==", parseInt(season)),
      where("status", "==", "pending")
    );

    const gamesSnapshot = await getDocs(q);

    if (gamesSnapshot.empty) {
      alert("No pending games to schedule. Please save games as pending first.");
      return;
    }

    const batch = writeBatch(db);

    gamesSnapshot.docs.forEach(docSnap => {
      const gameRef = doc(db, "games", docSnap.id);
      batch.update(gameRef, { status: "scheduled" });
    });

    await batch.commit();
    alert(`${gamesSnapshot.docs.length} pending games updated to scheduled.`);
  }

  const generateGamesCSV = () => {
    if (games.length === 0) {
      alert("No games to generate CSV.");
      return;
    }

    // Map each team to the set of dates they are playing
    const teamGameDates = {};
    teams.forEach(t => {
      teamGameDates[t.id] = new Set(
        games
          .filter(g => g.homeTeamID === t.id || g.awayTeamID === t.id)
          .map(g => new Date(g.date).toDateString()) // only date, ignore time
      );
    });

    // Assign scorer teams with sequential fairness and no conflicts
    const assignScorersSequential = (gamesList, eligibleTeams) => {
      const scorerCount = {};
      const scorerDates = {}; // teamID → dates already assigned to score
      eligibleTeams.forEach(t => { scorerCount[t.id] = 0; scorerDates[t.id] = new Set(); });

      return gamesList.map(g => {
        const gameDateStr = new Date(g.date).toDateString();

        // Filter out teams that are playing on this date. Prefer teams not
        // already scoring that day (so one team isn't sent to two gyms), then
        // the team that has scored the fewest games so far.
        const eligibleForGame = eligibleTeams
          .filter(
            t =>
              t.id !== g.homeTeamID &&
              t.id !== g.awayTeamID &&
              !teamGameDates[t.id].has(gameDateStr)
          )
          .sort((a, b) =>
            (scorerDates[a.id].has(gameDateStr) - scorerDates[b.id].has(gameDateStr)) ||
            (scorerCount[a.id] - scorerCount[b.id])
          );

        if (eligibleForGame.length === 0) {
          throw new Error(
            `No eligible scorer team for game ${g.homeTeamID} vs ${g.awayTeamID} on ${gameDateStr}`
          );
        }

        const scorer = eligibleForGame[0];
        scorerCount[scorer.id]++;
        scorerDates[scorer.id].add(gameDateStr);

        return { ...g, scoringTeam: scorer.id };
      });
    };

    // Conference seasons: each conference scores its own gym's games (East at
    // SBCA, West at B&GC). Without conferences, every team is in one scorer
    // pool across all gyms so scoring duty is balanced league-wide.
    let gamesWithScorers;
    try {
      if (seasonHasConferences) {
        const poolFor = (location) =>
          location === 'sbca' ? teams.filter(t => t.conference === 'east')
          : location === 'b&gc' ? teams.filter(t => t.conference === 'west')
          : teams;
        const locations = [...new Set(games.map(g => g.location))];
        gamesWithScorers = locations.flatMap(loc =>
          assignScorersSequential(games.filter(g => g.location === loc), poolFor(loc))
        );
      } else {
        gamesWithScorers = assignScorersSequential([...games].sort((a, b) => a.date - b.date), teams);
      }
    } catch (err) {
      alert(err.message);
      return;
    }

    // CSV generation
    const makeCSV = (filteredGames, label) => {
      let csvContent =
        "Date,Time,Location,Teams,Referee,Scoring Team,Clock,Book\n";

      filteredGames.forEach(g => {
        const dateObj = new Date(g.date);

        const dateStr = dateObj.toLocaleDateString("en-US", {
          weekday: "long",
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });

        const timeStr = dateObj.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        const locationStr =
          g.location === "sbca"
            ? "SBCA, 866 E Broadway, Boston MA 02127"
            : g.location === "b&gc"
            ? "B&GC, 230 W Sixth Street, Boston MA 02127"
            : utils.getLocationName(g.location);

        const homeTeam = teams.find(t => t.id === g.homeTeamID)?.name || g.homeTeamID;
        const awayTeam = teams.find(t => t.id === g.awayTeamID)?.name || g.awayTeamID;

        const csvTeams = `${homeTeam} vs. ${awayTeam}`;

        const scoringTeamName = teams.find(t => t.id === g.scoringTeam)?.name || "";

        csvContent += `"${dateStr}","${timeStr}","${locationStr}","${csvTeams}","","${scoringTeamName}","",""\n`;
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `season_${season}_games_${label}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // One CSV per venue
    [...new Set(gamesWithScorers.map(g => g.location))].forEach(loc => {
      makeCSV(gamesWithScorers.filter(g => g.location === loc).sort((a, b) => a.date - b.date), loc);
    });
  };

  const buildTeamRestrictionsHash = () => {
    const hash = {};
    for (const team of teams) {
      const r = teamRestrictions[team.id];
      const hasRestriction = r && r.type && r.type !== 'none' && r.days && r.days.length > 0;
      hash[team.id] = hasRestriction ? r : {};
    }
    return hash;
  };

  const saveScheduleConfig = async () => {
    await setDoc(doc(db, 'seasons', season), {
      scheduleConfig: { ...scheduleConfig, teamRestrictions: buildTeamRestrictionsHash() },
      updatedAt: serverTimestamp()
    }, { merge: true });
    alert('Schedule config saved.');
  };

  const saveTeamRestrictions = async () => {
    await setDoc(doc(db, 'seasons', season), {
      scheduleConfig: { ...scheduleConfig, teamRestrictions: buildTeamRestrictionsHash() },
      updatedAt: serverTimestamp()
    }, { merge: true });
    alert('Team restrictions saved.');
  };

  const missingConference = seasonHasConferences &&
    teams.some(t => !t.conference || (t.conference !== 'east' && t.conference !== 'west'));
  // Auto-generation builds one round-robin per conference (East at SBCA, West
  // at B&GC), so it only applies to conference seasons; otherwise import a CSV.
  const canGenerate = seasonHasConferences && !missingConference;

  if (dataReady == false) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Schedule">
        <div className="league-content">
          <div className="league-body">
            <p className='league-admin-back-button'>
              <a className='league-link' href="/admin"><b>← Back to Admin Home</b></a>
            </p>
            <p className='league-admin-back-button'>
              <a className='league-link' href={"/admin/seasons/" + season}><b>← Back to Season { season }</b></a>
            </p>

            <p className="league-title-standard-secondary text-align-center">
              { games.length == 0 ? 'Create' : 'Edit' } Games
            </p>

            {missingConference && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                ⚠️ Some teams are missing a conference assignment. <a href={`/admin/seasons/${season}`}>Set conferences</a> before generating a schedule.
              </div>
            )}

            {!seasonHasConferences && (
              <div style={{ background: '#e7f1ff', border: '1px solid #9ec5fe', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                ℹ️ Season {season} has no conferences, so all teams are in one league-wide pool. Auto-generating a schedule only works for East/West conference seasons. Use <strong>Import Schedule CSV</strong> below instead.
              </div>
            )}

            {/* Workflow steps */}
            <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, lineHeight: '1.8' }}>
                <span><strong>Step 1 – Edit Schedule Config / Team Day Restrictions:</strong> Set start date, blackout dates, game days, and per-team day constraints.</span>
                <span><strong>Step 2 – {games.length > 0 ? 'Regenerate Temp Games' : 'Generate Temp Games'} or Import Schedule CSV:</strong> Generates the schedule in memory, or loads it from a CSV file. Redo as many times as needed before saving.</span>
                <span><strong>Step 3 – Save Temp Games as Pending:</strong> Writes generated games to the database. Safe to redo after regenerating.</span>
                <span><strong>Step 4 – Save Pending Games as Scheduled:</strong> Flips all pending games to scheduled, making them visible on the public schedule.</span>
                <span><strong>Step 5 – Export Games CSV:</strong> Downloads a CSV per venue with scorer assignments for printing or distribution.</span>
              </div>
            </div>

            {/* Schedule Config collapsible section */}
            <div style={{ marginBottom: '1em', border: '1px solid #dee2e6', borderRadius: 6 }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ width: '100%', textAlign: 'left', borderRadius: 6, padding: '8px 14px' }}
                onClick={() => setShowScheduleConfig(v => !v)}
              >
                {showScheduleConfig ? '▼' : '▶'} Edit Schedule Config
              </button>
              {showScheduleConfig && (
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ marginRight: 8 }}><strong>Start Date:</strong></label>
                    <input
                      type="date"
                      value={scheduleConfig.startDate}
                      onChange={e => setScheduleConfig(sc => ({ ...sc, startDate: e.target.value }))}
                    />
                    <label style={{ marginLeft: 16, marginRight: 8 }}><strong>Start Time:</strong></label>
                    <input
                      type="time"
                      value={scheduleConfig.startTime}
                      onChange={e => setScheduleConfig(sc => ({ ...sc, startTime: e.target.value }))}
                    />
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <strong>Game Days:</strong>
                    <div style={{ marginTop: 4 }}>
                      {ALL_DAYS_OF_WEEK.map(day => (
                        <label key={day} style={{ marginRight: 14 }}>
                          <input
                            type="checkbox"
                            checked={scheduleConfig.gameDays.includes(day)}
                            onChange={e => {
                              if (e.target.checked) {
                                setScheduleConfig(sc => ({ ...sc, gameDays: [...sc.gameDays, day] }));
                              } else {
                                setScheduleConfig(sc => ({ ...sc, gameDays: sc.gameDays.filter(d => d !== day) }));
                              }
                            }}
                            style={{ marginRight: 4 }}
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <strong>Blackout Dates:</strong>
                    <ul style={{ marginTop: 4, marginBottom: 6 }}>
                      {scheduleConfig.blackoutDates.map((d, i) => (
                        <li key={i}>
                          {d}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            style={{ marginLeft: 8 }}
                            onClick={() => setScheduleConfig(sc => ({ ...sc, blackoutDates: sc.blackoutDates.filter((_, j) => j !== i) }))}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div>
                      <input
                        type="date"
                        value={newBlackoutDate}
                        onChange={e => setNewBlackoutDate(e.target.value)}
                        style={{ marginRight: 8 }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          if (newBlackoutDate && !scheduleConfig.blackoutDates.includes(newBlackoutDate)) {
                            setScheduleConfig(sc => ({ ...sc, blackoutDates: [...sc.blackoutDates, newBlackoutDate] }));
                            setNewBlackoutDate('');
                          }
                        }}
                      >
                        Add Date
                      </button>
                    </div>
                  </div>
                  <button type="button" className="btn btn-primary" onClick={saveScheduleConfig}>
                    Save Schedule Config
                  </button>
                </div>
              )}
            </div>

            {/* Team Restrictions collapsible section */}
            <div style={{ marginBottom: '1em', border: '1px solid #dee2e6', borderRadius: 6 }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ width: '100%', textAlign: 'left', borderRadius: 6, padding: '8px 14px' }}
                onClick={() => setShowTeamRestrictions(v => !v)}
              >
                {showTeamRestrictions ? '▼' : '▶'} Team Day Restrictions
              </button>
              {showTeamRestrictions && (
                <div style={{ padding: '14px 16px' }}>
                  {teams.map(team => {
                    const restriction = teamRestrictions[team.id] || { type: 'none', days: [] };
                    const isFrPeter = frPeterTeamID === team.id;
                    return (
                      <div key={team.id} style={{ marginBottom: 14, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                        <strong>{team.name}</strong>
                        {isFrPeter && <span style={{ marginLeft: 8, color: '#666', fontStyle: 'italic' }}>(Fr. Peter's team)</span>}
                        <div style={{ marginTop: 4 }}>
                          {['none', 'whitelist', 'blacklist'].map(type => (
                            <label key={type} style={{ marginRight: 16 }}>
                              <input
                                type="radio"
                                name={`restriction-type-${team.id}`}
                                value={type}
                                checked={(restriction.type || 'none') === type}
                                onChange={() => setTeamRestrictions(tr => ({
                                  ...tr,
                                  [team.id]: { ...restriction, type, days: type === 'none' ? [] : (restriction.days || []) }
                                }))}
                                style={{ marginRight: 4 }}
                              />
                              {type === 'none' ? 'No restriction' : type === 'whitelist' ? 'Whitelist (only these days)' : 'Blacklist (not these days)'}
                            </label>
                          ))}
                        </div>
                        {(restriction.type === 'whitelist' || restriction.type === 'blacklist') && (
                          <div style={{ marginTop: 4, marginLeft: 8 }}>
                            {scheduleConfig.gameDays.map(day => (
                              <label key={day} style={{ marginRight: 12 }}>
                                <input
                                  type="checkbox"
                                  checked={(restriction.days || []).includes(day)}
                                  onChange={e => {
                                    const currentDays = restriction.days || [];
                                    const newDays = e.target.checked
                                      ? [...currentDays, day]
                                      : currentDays.filter(d => d !== day);
                                    setTeamRestrictions(tr => ({
                                      ...tr,
                                      [team.id]: { ...restriction, days: newDays }
                                    }));
                                  }}
                                  style={{ marginRight: 4 }}
                                />
                                {day}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button type="button" className="btn btn-primary" onClick={saveTeamRestrictions}>
                    Save Restrictions
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "0.75em" }}>
              <label style={{ marginRight: 8 }}>
                Number of games per team:{" "}
                <input
                  type="number"
                  min="1"
                  value={gamesPerTeam}
                  disabled
                  onChange={handleGamesPerTeamChange}
                  style={{ marginRight: 8 }}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => askConfirm(
                  games.length > 0
                    ? 'Regenerate the temp schedule? The current unsaved schedule will be replaced.'
                    : 'Generate a temp schedule? This stays in memory until you save as pending.',
                  createSchedule
                )}
                disabled={!gamesEditable || !canGenerate}
              >
                {games.length > 0 ? 'Regenerate Temp Games' : 'Generate Temp Games'}
              </button>
            </div>

            {/* Schedule CSV import */}
            <div style={{ marginBottom: '0.75em', border: '1px solid #dee2e6', borderRadius: 6, padding: '12px 16px' }}>
              <strong>Import Schedule CSV</strong>
              <div style={{ color: '#555', fontSize: 14, margin: '4px 0 8px' }}>
                In Excel, use <em>File → Save As → CSV UTF-8</em>. Needs columns <strong>Date</strong>, <strong>Home</strong>, <strong>Away</strong> (or one <strong>Teams</strong> column like "Home vs. Away"), plus optional <strong>Time</strong> and <strong>Location</strong>. Teams must match a season team's name or abbreviation exactly. Location can be SBCA, B&amp;GC, Tynan or Condon; rows without one use the default location below. Times are Eastern; a blank time uses the start time from Schedule Config. Importing only replaces the temp games; nothing is saved until Step 3.
                {' '}<button type="button" className="btn btn-link btn-sm p-0" style={{ verticalAlign: 'baseline' }} onClick={downloadImportTemplate}>Download template</button>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ marginRight: 8 }}><strong>Default location:</strong></label>
                <select value={importLocation} onChange={e => setImportLocation(e.target.value)}>
                  {LOCATION_ALIASES.map(([loc]) => (
                    <option key={loc} value={loc}>{utils.getLocationName(loc)}</option>
                  ))}
                </select>
              </div>
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={!gamesEditable}
                onChange={e => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = ''; // allow re-selecting the same file after fixing it
                  if (!file) return;
                  const run = () => importScheduleCSV(file);
                  if (games.length > 0) {
                    askConfirm(`Replace the ${games.length} games currently shown with the games in ${file.name}? Nothing is saved until you save as pending.`, run);
                  } else {
                    run();
                  }
                }}
              />
              {!gamesEditable && (
                <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>Disabled because this season already has scheduled or completed games.</div>
              )}
              {importResult && importResult.errors.length > 0 && (
                <div style={{ background: '#fdecea', border: '1px solid #f5c2c7', borderRadius: 6, padding: '8px 12px', marginTop: 10 }}>
                  <strong>Couldn't import {importResult.fileName}.</strong> Fix these and re-import (no games were changed):
                  <ul style={{ margin: '4px 0 0' }}>
                    {importResult.errors.slice(0, 25).map((err, i) => <li key={i}>{err}</li>)}
                    {importResult.errors.length > 25 && <li>…and {importResult.errors.length - 25} more</li>}
                  </ul>
                </div>
              )}
              {importResult && importResult.errors.length === 0 && (
                <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 12px', marginTop: 10 }}>
                  Imported {importResult.count} games from {importResult.fileName}. Review them below, then <strong>Save Temp Games as Pending</strong>.
                  {importResult.warnings.length > 0 && (
                    <>
                      <div style={{ marginTop: 6 }}><strong>Check these:</strong></div>
                      <ul style={{ margin: '4px 0 0' }}>
                        {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "0.75em" }}>
              <button type="button" className="btn btn-warning" onClick={() => askConfirm('Save temp games as pending? Any existing pending games for this season will be deleted and replaced.', saveGamesAsPending)} disabled={!gamesEditable || missingConference}>
                Save Temp Games as Pending
              </button>
            </div>

            <div style={{ marginBottom: "0.75em" }}>
              <button type="button" className="btn btn-success" onClick={() => askConfirm('Mark all pending games as scheduled? This makes them visible on the public schedule.', saveGames)} disabled={!gamesEditable || missingConference}>
                Save Pending Games as Scheduled
              </button>
            </div>

            <div style={{ marginBottom: "1em" }}>
              <button type="button" className="btn btn-dark" onClick={() => askConfirm('Export a CSV file for each venue?', generateGamesCSV)} disabled={missingConference}>
                Export Games CSV
              </button>
            </div>
            <div>
            {
              games.length > 0 && (() => {
                const blackoutDatesSet = new Set(
                  scheduleConfig.blackoutDates.map(d => new Date(d).toDateString())
                );

                return teams.map(team => {
                  const teamGames = games.filter(
                    g => g.homeTeamID === team.id || g.awayTeamID === team.id
                  );

                  const weekdaysCount = { Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0 };

                  teamGames.forEach(g => {
                    if (g.weekday) weekdaysCount[g.weekday] = (weekdaysCount[g.weekday] || 0) + 1;
                  });

                  return (
                    <div key={team.id} style={{ marginBottom: "16px" }}>
                      <strong>{team.name}</strong>{team.conference ? ` (${team.conference.toUpperCase()})` : ''} —
                      Total Games: {teamGames.length}
                      {frPeterTeamID === team.id && <span style={{ marginLeft: 8, color: '#666', fontStyle: 'italic' }}>(Fr. Peter's team)</span>}

                      <div style={{ marginLeft: "10px", marginTop: "5px" }}>
                        {Object.entries(weekdaysCount).map(([day, count]) => (
                          <span key={day} style={{ marginRight: "10px" }}>
                            {day}: {count}
                          </span>
                        ))}
                      </div>

                      {teamGames.length > 0 && (
                        <div style={{ marginLeft: "10px", marginTop: "5px" }}>
                          <em>Games:</em>
                          <ul style={{ marginTop: "4px" }}>
                            {teamGames.map((g, i) => {
                              const opponentId = g.homeTeamID === team.id ? g.awayTeamID : g.homeTeamID;
                              const opponent = teams.find(t => t.id === opponentId);
                              const opponentConf = opponent?.conference ? ` (${opponent.conference.toUpperCase()})` : '';
                              const homeAway = g.homeTeamID === team.id ? "Home" : "Away";
                              const nonConf = seasonHasConferences && g.homeConference !== g.awayConference ? " (Non-Conf)" : "";

                              // Check if this game's date is in the blackout dates
                              const isBlackout = blackoutDatesSet.has(g.date.toDateString());

                              return (
                                <li key={i} style={{ color: isBlackout ? "red" : "inherit" }}>
                                  Week {g.week}: {g.location} -- {homeAway} vs {opponent?.name}{opponentConf}{nonConf} - {g.weekday}, {new Date(g.date).toLocaleDateString("en-US", { month: 'short', day: 'numeric' })}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                });
              })()
            }


            </div>
            <div className="league-table-container schedule">
              <ul>
                {games.map((g, i) => (
                  <li key={i}>
                    <strong>{formatDate(g.date)}</strong> ({g.location}) — {g.homeTeamID} vs {g.awayTeamID}
                    <br/>
                    {g.id && <small style={{ marginLeft: 8, color: '#999' }}>{g.id}</small>}
                  </li>
                ))}
              </ul>
            </div>
          {/* ── Calendar views ── */}
          {games.length > 0 && (() => {
          const DOW_ABBV = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
          const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

          const renderCalendar = (venue, title) => {
            const { year, month } = calDate;
            const venueGames = games.filter(g => g.location === venue);
            const firstDow = new Date(year, month, 1).getDay();
            const daysInMonth = new Date(year, month + 1, 0).getDate();

            // Build weeks as arrays of day-numbers (null = empty cell)
            const cells = [...Array(firstDow).fill(null),
                           ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
            while (cells.length % 7 !== 0) cells.push(null);
            const weeks = [];
            for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

            const cellBase = { border: '1px solid #e0e0e0', padding: '8px 10px', verticalAlign: 'top', minHeight: 110, width: '14.28%' };

            const selIsHere = selectedGame && selectedGame.location === venue;

            return (
              <div>
                <h4 style={{ marginBottom: 8 }}>{title}</h4>

                {/* Month nav */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <button className="btn btn-sm btn-outline-secondary"
                    disabled={calIdx <= 0}
                    onClick={() => setCalDate(calMonths[calIdx - 1])}>←</button>
                  <strong>{MONTH_NAMES[month]} {year}</strong>
                  <button className="btn btn-sm btn-outline-secondary"
                    disabled={calIdx >= calMonths.length - 1}
                    onClick={() => setCalDate(calMonths[calIdx + 1])}>→</button>
                </div>


                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>{DOW_ABBV.map(d => (
                      <th key={d} style={{ ...cellBase, background: '#f5f5f5', fontSize: 13, textAlign: 'center', fontWeight: 600 }}>{d}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {weeks.map((week, wi) => (
                      <tr key={wi}>
                        {week.map((day, di) => {
                          if (!day) return <td key={di} style={{ ...cellBase, background: '#fafafa' }} />;

                          const date = new Date(year, month, day);
                          const dow = date.getDay();
                          const isWeekend = dow === 0 || dow === 6;
                          const dayGames = venueGames.filter(g => {
                            const gd = g.date;
                            return gd.getFullYear() === year && gd.getMonth() === month && gd.getDate() === day;
                          });
                          const isEmpty = dayGames.length === 0;
                          const isDropTarget = canEditCalendar && selectedGame && selectedGame.location === venue && isEmpty && !isWeekend;
                          const isBlackoutDay = scheduleConfig.blackoutDates.some(bd => {
                            const [yr, mo, dy] = bd.split('-').map(Number);
                            return yr === year && mo - 1 === month && dy === day;
                          });

                          // When a game is selected, mark days where its teams already play
                          const homePlayingToday = selIsHere && games.some(g =>
                            g !== selectedGame &&
                            (g.homeTeamID === selectedGame.homeTeamID || g.awayTeamID === selectedGame.homeTeamID) &&
                            g.date.getFullYear() === year && g.date.getMonth() === month && g.date.getDate() === day
                          );
                          const awayPlayingToday = selIsHere && games.some(g =>
                            g !== selectedGame &&
                            (g.homeTeamID === selectedGame.awayTeamID || g.awayTeamID === selectedGame.awayTeamID) &&
                            g.date.getFullYear() === year && g.date.getMonth() === month && g.date.getDate() === day
                          );

                          let cellBorder = '1px solid #e0e0e0';
                          let cellBg = isBlackoutDay ? '#fdecea' : isWeekend ? '#fafafa' : '#fff';
                          if (isDropTarget && !isBlackoutDay) {
                            cellBg = '#e8f5e9';
                          } else if (homePlayingToday) {
                            cellBg = '#fff8e1';
                          } else if (awayPlayingToday) {
                            cellBg = '#e3f2fd';
                          }

                          return (
                            <td
                              key={di}
                              style={{
                                ...cellBase,
                                background: cellBg,
                                border: cellBorder,
                                cursor: isDropTarget ? 'pointer' : 'default',
                              }}
                              onClick={() => { if (isDropTarget) rescheduleGame(selectedGame, day); }}
                            >
                              <div style={{ fontSize: 13, color: isBlackoutDay ? '#c00' : '#888', marginBottom: 4, fontWeight: 600 }}>{day}</div>
                              {dayGames.map((game, gi) => {
                                const home = teams.find(t => t.id === game.homeTeamID);
                                const away = teams.find(t => t.id === game.awayTeamID);
                                const timeStr = game.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                const isSelected = selectedGame === game;
                                return (
                                  <div
                                    key={gi}
                                    title="Click to select for rescheduling"
                                    style={{
                                      fontSize: 13, lineHeight: 1.4,
                                      background: isSelected ? '#fff3cd' : '#e3f2fd',
                                      border: `1px solid ${isSelected ? '#ffc107' : '#90caf9'}`,
                                      borderRadius: 3, padding: '2px 4px', marginBottom: 2,
                                      cursor: canEditCalendar ? 'pointer' : 'default',
                                    }}
                                    onClick={e => { e.stopPropagation(); if (canEditCalendar) setSelectedGame(isSelected ? null : game); }}
                                  >
                                    <div style={{ fontWeight: 600 }}>
                                      {home?.nameAbbv || game.homeTeamID} vs {away?.nameAbbv || game.awayTeamID}
                                    </div>
                                    <div style={{ color: '#555' }}>{timeStr}</div>
                                  </div>
                                );
                              })}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          };

          // One calendar per venue in use, SBCA and B&GC first.
          const VENUE_ORDER = ['sbca', 'b&gc'];
          const venues = [...new Set(games.map(g => g.location))].sort((a, b) =>
            (VENUE_ORDER.indexOf(a) + 1 || 99) - (VENUE_ORDER.indexOf(b) + 1 || 99)
          );
          const venueTitle = (venue) => {
            const name = utils.getLocationName(venue, true);
            if (!seasonHasConferences) return name;
            if (venue === 'sbca') return `East — ${name}`;
            if (venue === 'b&gc') return `West — ${name}`;
            return name;
          };

          return (
            <>
              {venues.map((venue, i) => (
                <div key={venue} className="league-table-container schedule" style={i > 0 ? { marginTop: 40 } : undefined}>
                  {renderCalendar(venue, venueTitle(venue))}
                </div>
              ))}
            </>
          );
        })()}
          </div>
        </div>

        <Footer/>

        {confirm.open && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
            onClick={closeConfirm}
          >
            <div
              style={{ background: '#fff', borderRadius: 8, padding: '24px 28px', maxWidth: 420, width: '90%' }}
              onClick={e => e.stopPropagation()}
            >
              <p style={{ marginBottom: 20 }}>{confirm.message}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline-secondary btn-sm" onClick={closeConfirm}>Cancel</button>
                <button className="btn btn-dark btn-sm" onClick={() => { closeConfirm(); confirm.onConfirm(); }}>Confirm</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminSeasonGames;
